import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { Back, Power0, TweenMax } from "gsap";
import { Ref, ref, shallowRef, watch } from "vue";
import ForceGraph3D, { ForceGraph3DInstance } from "3d-force-graph";
import { CSS2DRenderer } from "three/examples/jsm/renderers/CSS2DRenderer";
import { debug as d } from "debug";
import {
  leaderTypeLayout,
  linedUpHorizontally,
  origin,
  teamAroundIndividualLeaderTypeLayout,
} from "./node-layouts";
import { HeadNode, SoloOrViewpointsOption } from "./nodes-and-links-models";
import { NodeAndLinksData } from "./use-nodes-and-links-data";
import createHead3d, { graphOptions } from "./3d-node-renderer";
import { createLink3d, updateLinkPosition3d } from "./3d-link-renderer";
import {
  animateNodesToPositions,
  disableSimulationForces,
  freezeSimulation,
  hideLinks,
  setAllNodePositions,
  setControlsEnabled,
  showLinks,
  unfreezeSimulation,
} from "./3d-graph";
import {
  positionCameraForNodes,
  positionCameraForSingleNode,
  zoomCameraToDistanceForSingleNode,
} from "./3d-camera";

const debug = d("cs.heatmaps");

type NodesAndLinksGraph = {
  graph: Ref<ForceGraph3DInstance | undefined>;
  selectedNode: Ref<HeadNode | null>;
  hoveredNode: Ref<HeadNode | null>;
  autoRotate: Ref<boolean>;
  tooltipRenderer: CSS2DRenderer;
};

export const transitionMilliseconds = 500;

const maxNodesToDisplayLinksFor = 20;

export default function useNodesAndLinksGraph(
  heatmapData: Ref<NodeAndLinksData>,
  canvasWrapper: Ref<HTMLElement | undefined>,
  size: Ref<{ width: number; height: number } | undefined>,
  isResizing: Ref<boolean>,
  soloOrViewpoints: Ref<SoloOrViewpointsOption>,
): NodesAndLinksGraph {
  const graph = shallowRef<ForceGraph3DInstance>();
  const autoRotate = ref(false);
  const hoveredNode = ref<HeadNode | null>(null);
  const selectedNode = ref<HeadNode | null>(null);
  const tooltipRenderer = new CSS2DRenderer();

  function setAutoRotate(enable: boolean) {
    autoRotate.value = enable;
    const controls = graph.value!.controls() as OrbitControls;
    controls.autoRotate = enable;
  }

  function linkVisibilityHandler(link: { source?: any; target?: any }): boolean {
    if (heatmapData.value && heatmapData.value.nodes.length > maxNodesToDisplayLinksFor) {
      return false; // Don't display too many links
    }

    if (selectedNode.value) {
      return link.source === selectedNode.value || link.target === selectedNode.value;
    }
    return true;
  }

  function update(skipAnimation = false) {
    const transitionMs = skipAnimation ? 0 : transitionMilliseconds;
    debug("update", { width: size.value!.width, height: size.value!.height });
    const existingData = graph.value!.graphData();
    const newData = heatmapData.value;
    let updateType: string;
    if (existingData.nodes.length === 0) {
      updateType = "initial";
    } else {
      updateType = newData.nodes.length > 1 ? "expanding" : "contracting";
    }

    const myGraph = graph.value!;

    if (updateType === "initial") {
      setAutoRotate(false);
      myGraph.graphData(newData);
      disableSimulationForces(myGraph);
      positionCameraForSingleNode(myGraph, 0);
      setControlsEnabled(myGraph, false);
    } else if (updateType === "expanding") {
      myGraph.graphData(newData);
      const { nodes, links } = myGraph.graphData() as any;
      disableSimulationForces(myGraph);
      setAllNodePositions(nodes, origin());
      const positions = leaderTypeLayout(nodes, links, soloOrViewpoints.value);
      positionCameraForNodes(myGraph, positions, transitionMs);
      animateNodesToPositions(
        myGraph,
        nodes,
        {
          transitionMs,
          commonInitialPosition: origin(),
          individualFinalPositions: positions,
          hideLinksDuringTransition: true,
        },
        () => {
          debug("COMPLETED EXPANSION ANIMATION");
          showLinks(myGraph, linkVisibilityHandler);
          setAutoRotate(true);
          setControlsEnabled(myGraph, true);
        },
      );
    } else if (updateType === "contracting") {
      zoomCameraToDistanceForSingleNode(myGraph, transitionMs);
      setAutoRotate(false);
      animateNodesToPositions(
        myGraph,
        existingData.nodes,
        { transitionMs, commonFinalPosition: origin(), hideLinksDuringTransition: true },
        () => {
          debug("COMPLETED CONTRACTION ANIMATION");
          positionCameraForSingleNode(myGraph, 0);
          myGraph.graphData(newData);
          setAutoRotate(false);
          setControlsEnabled(myGraph, false);
        },
      );
    } else {
      throw new Error(`Unexpected update type: ${updateType}`);
    }
  }

  function onNodeSelected(node: any) {
    const myGraph = graph.value!;
    selectedNode.value = node;
    const data = myGraph.graphData();
    const nodes = data.nodes as HeadNode[];
    const positions = teamAroundIndividualLeaderTypeLayout(
      (node as any).id,
      nodes,
      data.links,
      soloOrViewpoints.value,
    );
    positionCameraForNodes(myGraph, positions);
    animateNodesToPositions(
      myGraph,
      nodes,
      {
        transitionMs: 200,
        delayMs: 10,
        individualFinalPositions: positions,
        hideLinksDuringTransition: true,
      },
      () => {
        debug("COMPLETED SELECTION ANIMATION");
        showLinks(myGraph, linkVisibilityHandler);
        setControlsEnabled(myGraph, false);
      },
    );
  }

  function onNodeUnselected() {
    const myGraph = graph.value!;

    // Deselect
    selectedNode.value = null;

    // Position the nodes back based on strengths
    const data = myGraph.graphData();
    const nodes = data.nodes as HeadNode[];
    const positions = leaderTypeLayout(nodes, data.links, soloOrViewpoints.value);
    setAutoRotate(true);
    positionCameraForNodes(myGraph, positions, 200);
    animateNodesToPositions(
      myGraph,
      nodes,
      { transitionMs: 200, delayMs: 10, individualFinalPositions: positions },
      () => {
        debug("COMPLETED DE-SELECTION ANIMATION");
        showLinks(myGraph, linkVisibilityHandler); // Show all the links again (not just to selected node)
        setControlsEnabled(myGraph, true);
      },
    );
  }

  function initialize() {
    debug("initialize");
    const myGraph = ForceGraph3D({
      controlType: "orbit",
      rendererConfig: {
        alpha: true,
        antialias: true,
        depth: true,
      },
      extraRenderers: [tooltipRenderer as any],
    });
    graph.value = myGraph;
    myGraph(canvasWrapper.value!);

    myGraph
      .width(size.value!.width)
      .height(size.value!.height)
      .numDimensions(3)
      .enableNavigationControls(true)
      .enablePointerInteraction(true)
      .enableNodeDrag(false)
      .showNavInfo(false)
      .backgroundColor("#ffffff")
      .nodeLabel(() => "")
      .nodeRelSize(1)
      .linkWidth(5)
      .linkThreeObject(createLink3d as any)
      .linkPositionUpdate(updateLinkPosition3d as any)
      .d3AlphaDecay(1 - 0.001 ** (1 / 300000))
      .d3AlphaMin(0.001);

    tooltipRenderer.setSize(size.value!.width, size.value!.height);

    const controls = myGraph.controls() as OrbitControls;
    controls.addEventListener("start", () => {
      setAutoRotate(false);
    });

    myGraph.nodeThreeObject(createHead3d as any);
    myGraph.scene().onBeforeRender = (renderer, scene, camera) => {
      if (heatmapData.value.nodes.length === 1) return; // TODO: Disable panning for team/aggregate head too
      const firstNode = scene.children[4]?.children.find((o) => o.name.startsWith("node-"));
      if (!firstNode) return;
      const angleBetween = firstNode.quaternion.angleTo(camera.quaternion);
      if (angleBetween > Math.PI / 180) {
        // Rotate to face the camera
        scene.children[4]?.children
          .filter((o) => o.name.startsWith("node-"))
          .forEach((o) => {
            o.quaternion.copy(camera.quaternion);
          });
      }
    };

    myGraph.onNodeHover((node, prevNode) => {
      hoveredNode.value = node as any;
    });

    myGraph.onNodeClick((node, prevNode) => {
      if (heatmapData.value?.nodes.length === 1) return;
      if (node === selectedNode.value) {
        onNodeUnselected();
        return;
      }

      onNodeSelected(node);
    });

    debug("INITIALIZED GRAPH", myGraph);
    graphOptions.size = size.value;
    update();
  }

  watch(size, (newValue, prevValue) => {
    const { width, height } = newValue!;
    graphOptions.size = newValue;
    debug("graph resize", { width, height });
    if (!prevValue) initialize();
    else {
      graph.value!.width(width).height(height);
      graph.value!.refresh();
      // update(true);
    }
  });

  watch(heatmapData, () => {
    update();
  });

  watch(isResizing, (newValue, prevValue) => {
    if (newValue) {
      graph.value?.pauseAnimation();
    } else {
      graph.value?.resumeAnimation();
    }
  });

  return {
    graph,
    selectedNode,
    hoveredNode,
    autoRotate,
    tooltipRenderer,
  };
}
